import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api-client.js';
import { ApiError } from '@/lib/api-error.js';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader, PageHeader } from '@/components/ui/Card.js';
import { Input } from '@/components/ui/Field.js';
import { RoleBadge } from '@/components/ui/Badge.js';
import { useToast } from '@/components/ui/Toast.js';
import { useAuth } from './auth-context.js';

/** Profil va parolni o'zgartirish. */
export function ProfilePage() {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const change = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post('/auth/change-password', body),
    onSuccess: async () => {
      toast.success('Parol o‘zgartirildi. Qaytadan kirishingiz kerak');
      // Server barcha seanslarni tugatadi — interfeys ham darhol chiqadi,
      // aks holda keyingi so'rov 401 bilan tushib qolardi.
      await logout();
    },
    onError: (error: Error) => {
      if (error instanceof ApiError) {
        if (error.code === 'WRONG_PASSWORD') {
          setErrors({ currentPassword: 'Joriy parol noto‘g‘ri' });
          return;
        }
        const fields = error.fieldErrors;
        if (Object.keys(fields).length > 0) {
          setErrors(fields);
          return;
        }
      }
      toast.error(error.message);
    },
  });

  if (!user) return null;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({});
    const form = new FormData(event.currentTarget);
    change.mutate({
      currentPassword: String(form.get('currentPassword') ?? ''),
      newPassword: String(form.get('newPassword') ?? ''),
    });
  };

  return (
    <>
      <PageHeader title="Profil" />

      <div className="max-w-xl space-y-6">
        <Card>
          <CardHeader title="Ma’lumotlar" />
          <CardBody className="space-y-2 text-sm">
            <Row label="F.I.SH" value={user.fullName} />
            <Row label="Elektron pochta" value={user.email} />
            <div className="flex justify-between gap-4 py-1">
              <span className="text-n-600">Rol</span>
              <RoleBadge role={user.role} />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Parolni o‘zgartirish"
            description="O‘zgartirilgach barcha qurilmalarda qayta kirish talab qilinadi"
          />
          <CardBody>
            <form onSubmit={onSubmit} className="space-y-4">
              <Input
                name="currentPassword"
                type="password"
                label="Joriy parol"
                required
                error={errors.currentPassword}
                autoComplete="current-password"
              />
              <Input
                name="newPassword"
                type="password"
                label="Yangi parol"
                required
                error={errors.newPassword}
                hint="Kamida 10 belgi, katta va kichik harf hamda raqam"
                autoComplete="new-password"
              />
              <Button type="submit" loading={change.isPending}>
                O‘zgartirish
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1">
      <span className="text-n-600">{label}</span>
      <span className="font-medium text-n-900">{value}</span>
    </div>
  );
}
